import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';

import { apiErrorMessage } from '../../shared/api/errors';
import { useDebouncedValue } from '../../shared/lib/useDebouncedValue';
import { inputClass } from '../../shared/ui/TextField';
import { Card, EmptyState, ErrorText, PageHeader, Spinner } from '../../shared/ui/primitives';
import { useSession } from '../auth/session';
import { listDoctorsDetailed } from './api';

const roleLabel: Record<string, string> = { admin: 'Администратор', doctor: 'Врач' };

export function DoctorsPage() {
  const { user } = useSession();
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim().toLowerCase(), 250);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'doctors'],
    queryFn: listDoctorsDetailed,
  });

  const rows = useMemo(() => {
    if (!data) return [];
    if (!search) return data;
    return data.filter((d) =>
      [d.fullName, d.email, d.phone, ...d.clinics].join(' ').toLowerCase().includes(search),
    );
  }, [data, search]);

  return (
    <section className="space-y-5">
      <PageHeader title="Врачи">
        <Link
          to="/admin/users/new"
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-600"
        >
          Добавить учётную запись
        </Link>
      </PageHeader>

      <input
        type="search"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        placeholder="Поиск по ФИО, e-mail, клинике"
        className={`${inputClass} sm:max-w-sm`}
      />

      {isLoading && <Spinner />}
      {isError && <ErrorText>{apiErrorMessage(error, 'Не удалось загрузить врачей')}</ErrorText>}
      {data && rows.length === 0 && <EmptyState>Ничего не найдено.</EmptyState>}

      {rows.length > 0 && (
        <Card className="divide-y divide-line overflow-hidden">
          {rows.map((d) => (
            <Link
              key={d.id}
              to={`/admin/doctors/${d.id}`}
              className="flex flex-col gap-2 px-4 py-3 transition-colors hover:bg-canvas sm:flex-row sm:items-center sm:justify-between sm:gap-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-ink">{d.fullName || d.email}</span>
                  <span className="rounded-full bg-canvas px-2 py-0.5 text-xs font-medium text-muted">
                    {roleLabel[d.role] ?? d.role}
                  </span>
                  {d.id === user?.id && <span className="text-xs text-muted">(вы)</span>}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-muted">
                  <span>{d.email}</span>
                  {d.phone && <span>{d.phone}</span>}
                  {d.clinics.length > 0 && <span>{d.clinics.join(', ')}</span>}
                </div>
              </div>
              <span className="shrink-0 text-sm font-medium text-accent">Пациенты: {d.patientCount}</span>
            </Link>
          ))}
        </Card>
      )}

      {data && <p className="text-xs text-muted">Всего врачей: {data.length}</p>}
    </section>
  );
}
